import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getClient, roleLabel, translateError, useSession, useStepUpRunner } from "@vixio/data";
import {
  Button, Callout, ConfirmDialog, DataTable, DescriptionList, Dialog, PageHeader, Panel, StatusPill, TextInput, formatDateTime, useToast,
} from "@vixio/ui";
import { useAuth } from "../auth/AuthProvider";
import { TotpEnrollForm } from "../auth/mfa";

interface FactorRow {
  id: string;
  friendly_name?: string;
  factor_type: string;
  status: string;
  created_at: string;
}

/** Conta de acesso. `variant="portal"`: senha e 2FA do cliente, com texto sem jargão e sem a lista de organizações. */
export function ProfilePage({ variant = "panel" }: { variant?: "panel" | "portal" }) {
  const portal = variant === "portal";
  const session = useSession();
  const { refreshAal, aal } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const run = useStepUpRunner();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [removing, setRemoving] = useState<FactorRow | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [endingOthers, setEndingOthers] = useState(false);

  const factors = useQuery({
    queryKey: ["auth", "factors", session.userId],
    queryFn: async () => {
      const { data, error } = await getClient().auth.mfa.listFactors();
      if (error) throw translateError(error);
      return (data?.all ?? []) as FactorRow[];
    },
  });
  const verified = (factors.data ?? []).filter((f) => f.status === "verified");

  return (
    <div className={portal ? undefined : "vx-content"}>
      {portal ? (
        <PageHeader
          title="Senha e segurança"
          description="Troque sua senha e, se quiser mais proteção, ative a verificação em duas etapas com um aplicativo autenticador no celular."
        />
      ) : (
        <PageHeader title="Meu perfil" description="Conta de acesso, organizações, senha e verificação em duas etapas." />
      )}
      <div className="vx-grid vx-grid--2">
        <Panel title="Conta">
          <DescriptionList
            items={[
              ["E-mail", session.email],
              ["Sessão", aal === "aal2" ? <StatusPill tone="ok">verificada com 2FA</StatusPill> : <StatusPill>senha ou link</StatusPill>],
              ...(portal ? [] : [["Administração da plataforma", session.isPlatformAdmin ? "Sim" : "Não"] as [string, string]]),
            ]}
          />
        </Panel>
        <Panel title="Trocar senha">
          <form
            className="vx-stack"
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              if (pw.length < 10) {
                toast.error("Senha curta", "Use pelo menos 10 caracteres.");
                return;
              }
              if (pw !== pw2) {
                toast.error("As senhas não conferem");
                return;
              }
              setPwBusy(true);
              try {
                await run(async () => {
                  const { error } = await getClient().auth.updateUser({ password: pw });
                  if (error) throw error;
                });
                setPw("");
                setPw2("");
                toast.ok("Senha alterada");
              } catch (err) {
                toast.error("Não foi possível trocar a senha", translateError(err).message);
              } finally {
                setPwBusy(false);
              }
            }}
          >
            <TextInput label="Nova senha" type="password" autoComplete="new-password" value={pw} onChange={setPw} hint="Mínimo de 10 caracteres." />
            <TextInput label="Repita a nova senha" type="password" autoComplete="new-password" value={pw2} onChange={setPw2} />
            <div className="vx-form-actions">
              <Button type="submit" loading={pwBusy}>
                Salvar senha
              </Button>
            </div>
          </form>
        </Panel>
      </div>

      <div className="vx-section">
        <Panel
          title="Verificação em duas etapas (TOTP)"
          actions={
            <Button size="sm" variant="primary" onClick={() => setEnrollOpen(true)}>
              {verified.length ? "Adicionar autenticador" : "Ativar 2FA"}
            </Button>
          }
          flush
        >
          {verified.length === 0 ? (
            <div style={{ padding: 16 }}>
              {portal ? (
                <Callout tone="info" title="Verificação em duas etapas desligada">
                  Com ela ligada, além da senha o acesso pede um código de 6 dígitos do aplicativo autenticador (Google
                  Authenticator, Microsoft Authenticator ou similar). Recomendamos ativar.
                </Callout>
              ) : (
                <Callout tone="warn" title="2FA desativado">
                  Estorno, anonimização, cancelamento de nota e mudança fiscal exigem 2FA. Ative agora ou quando o sistema
                  pedir.
                </Callout>
              )}
            </div>
          ) : null}
          <DataTable<FactorRow>
            bare
            caption="Autenticadores cadastrados"
            data={factors.data ?? []}
            loading={factors.isLoading}
            rowKey={(f) => f.id}
            empty="Nenhum autenticador cadastrado."
            columns={[
              { key: "friendly_name", header: "Nome", cell: (f) => f.friendly_name || "Autenticador" },
              { key: "status", header: "Situação", cell: (f) => (f.status === "verified" ? <StatusPill tone="ok">ativo</StatusPill> : <StatusPill tone="warn">não confirmado</StatusPill>) },
              { key: "created_at", header: "Cadastrado em", kind: "datetime" },
              {
                key: "actions",
                header: <span className="sr-only">Ações</span>,
                align: "right",
                csv: false,
                cell: (f) => (
                  <Button size="sm" variant="danger" onClick={() => setRemoving(f)}>
                    Remover
                  </Button>
                ),
              },
            ]}
          />
        </Panel>
      </div>

      <div className="vx-section">
        <Panel title="Sessões">
          <div className="vx-row vx-row--between" style={{ gap: "var(--sp-3)" }}>
            <p className="text-2" style={{ margin: 0, maxWidth: "60ch" }}>
              {portal
                ? "Entrou em outro computador ou celular e não saiu? Encerre as outras sessões: só esta continua aberta."
                : "Encerra o acesso em todos os outros navegadores e aparelhos; esta sessão continua aberta."}
            </p>
            <ConfirmDialogTrigger
              busy={endingOthers}
              onConfirm={async () => {
                setEndingOthers(true);
                try {
                  const { error } = await getClient().auth.signOut({ scope: "others" });
                  if (error) throw error;
                  toast.ok("Outras sessões encerradas", "Só esta sessão continua aberta.");
                } catch (err) {
                  toast.error("Não foi possível encerrar as outras sessões", translateError(err).message);
                } finally {
                  setEndingOthers(false);
                }
              }}
            />
          </div>
        </Panel>
      </div>

      {portal ? null : (
      <div className="vx-section">
        <Panel title="Organizações" flush>
          <DataTable
            bare
            caption="Minhas organizações"
            data={session.orgs}
            rowKey={(o) => o.id}
            columns={[
              { key: "name", header: "Organização", cell: (o) => <span className="vx-table__primary">{o.name}</span> },
              { key: "role", header: "Papel", cell: (o) => roleLabel(o.role) },
              {
                key: "current",
                header: <span className="sr-only">Atual</span>,
                align: "right",
                cell: (o) =>
                  o.id === session.org?.id ? (
                    <StatusPill tone="accent">atual</StatusPill>
                  ) : (
                    <Button size="sm" onClick={() => session.switchOrg(o.id)}>
                      Usar esta
                    </Button>
                  ),
              },
            ]}
          />
        </Panel>
      </div>
      )}

      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen} title="Cadastrar autenticador">
        {enrollOpen ? (
          <TotpEnrollForm
            onDone={async () => {
              setEnrollOpen(false);
              await refreshAal();
              await qc.invalidateQueries({ queryKey: ["auth"] });
              toast.ok("2FA ativado", "A sessão atual já está verificada.");
            }}
          />
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remover autenticador"
        confirmLabel="Remover"
        destructive
        confirmText="REMOVER"
        onConfirm={async () => {
          try {
            await run(async () => {
              const { error } = await getClient().auth.mfa.unenroll({ factorId: removing!.id });
              if (error) throw error;
            });
            await refreshAal();
            await qc.invalidateQueries({ queryKey: ["auth"] });
            toast.ok("Autenticador removido");
          } catch (err) {
            toast.error("Não foi possível remover", translateError(err).message);
            throw err;
          }
        }}
      >
        <p>
          Sem autenticador, operações sensíveis ficam bloqueadas até você cadastrar outro. Cadastrado em{" "}
          {formatDateTime(removing?.created_at)}.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function ConfirmDialogTrigger({ busy, onConfirm }: { busy: boolean; onConfirm: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button loading={busy} onClick={() => setOpen(true)}>
        Encerrar outras sessões
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Encerrar as outras sessões"
        confirmLabel="Encerrar"
        onConfirm={async () => {
          await onConfirm();
          setOpen(false);
        }}
      >
        <p>Quem estiver usando sua conta em outro navegador ou aparelho precisa entrar de novo com a senha.</p>
      </ConfirmDialog>
    </>
  );
}
